(() => {
  if (window.__awFaqAnchorLoaded) {
    return;
  }

  window.__awFaqAnchorLoaded = true;
  const animationDuration = 300;

  const instances = new Map();
  const selectors = {
    root: "[data-aw-faq-anchor]",
    navLink: ".aw-faq-anchor__nav-link",
    item: ".aw-faq-anchor__item",
    question: ".aw-faq-anchor__question",
    answer: ".aw-faq-anchor__answer",
  };

  function getOffset() {
    const header = document.querySelector("header, .site-header, .header, .aw-header");

    if (!header) {
      return 96;
    }

    return header.getBoundingClientRect().height + 24;
  }

  function scrollToTarget(target, updateHash = true) {
    if (!target) {
      return;
    }

    const top = target.getBoundingClientRect().top + window.pageYOffset - getOffset();

    window.scrollTo({
      top,
      behavior: "smooth",
    });

    if (updateHash && target.id) {
      window.history.replaceState(null, "", `#${target.id}`);
    }
  }

  class AWFaqAnchor {
    constructor(root) {
      this.root = root;
      this.links = Array.from(root.querySelectorAll(selectors.navLink));
      this.items = Array.from(root.querySelectorAll(selectors.item));
      this.handlers = [];

      this.bindEvents();
    }

    shouldAnimate() {
      return !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }

    stopAnimation(answer) {
      if (!answer) {
        return;
      }

      if (answer.__awFaqAnchorTransitionEnd) {
        answer.removeEventListener("transitionend", answer.__awFaqAnchorTransitionEnd);
        answer.__awFaqAnchorTransitionEnd = null;
      }

      answer.style.transition = "none";
      answer.style.height = `${answer.offsetHeight}px`;
      void answer.offsetHeight;
    }

    resetAnswerStyles(answer) {
      if (!answer) {
        return;
      }

      answer.style.height = "";
      answer.style.overflow = "";
      answer.style.transition = "";
    }

    bindEvents() {
      this.links.forEach((link) => {
        const handler = (event) => {
          const href = link.getAttribute("href");

          if (!href || !href.startsWith("#")) {
            return;
          }

          const target = this.root.querySelector(href);

          if (!target) {
            return;
          }

          event.preventDefault();
          scrollToTarget(target);
        };

        link.addEventListener("click", handler);
        this.handlers.push({ link, handler });
      });

      this.items.forEach((item) => {
        const question = item.querySelector(selectors.question);

        if (!question) {
          return;
        }

        const handler = (event) => {
          event.preventDefault();
          this.toggleItem(item);
        };

        question.addEventListener("click", handler);
        this.handlers.push({ link: question, handler });
      });
    }

    toggleItem(item) {
      if (item.open) {
        this.closeItem(item);
        return;
      }

      this.openItem(item);
    }

    openItem(item) {
      const answer = item.querySelector(selectors.answer);

      if (!answer) {
        item.open = true;
        return;
      }

      this.stopAnimation(answer);
      item.open = true;

      if (!this.shouldAnimate()) {
        this.resetAnswerStyles(answer);
        return;
      }

      answer.style.overflow = "hidden";
      answer.style.height = "0px";
      answer.style.transition = `height ${animationDuration}ms ease`;

      requestAnimationFrame(() => {
        answer.style.height = `${answer.scrollHeight}px`;
      });

      const onTransitionEnd = (event) => {
        if (event.target !== answer || event.propertyName !== "height") {
          return;
        }

        answer.removeEventListener("transitionend", onTransitionEnd);
        answer.__awFaqAnchorTransitionEnd = null;
        this.resetAnswerStyles(answer);
      };

      answer.__awFaqAnchorTransitionEnd = onTransitionEnd;
      answer.addEventListener("transitionend", onTransitionEnd);
    }

    closeItem(item) {
      const answer = item.querySelector(selectors.answer);

      if (!answer) {
        item.open = false;
        return;
      }

      this.stopAnimation(answer);

      if (!this.shouldAnimate()) {
        item.open = false;
        this.resetAnswerStyles(answer);
        return;
      }

      answer.style.overflow = "hidden";
      answer.style.height = `${answer.scrollHeight}px`;
      answer.style.transition = `height ${animationDuration}ms ease`;

      requestAnimationFrame(() => {
        answer.style.height = "0px";
      });

      const onTransitionEnd = (event) => {
        if (event.target !== answer || event.propertyName !== "height") {
          return;
        }

        answer.removeEventListener("transitionend", onTransitionEnd);
        answer.__awFaqAnchorTransitionEnd = null;
        item.open = false;
        this.resetAnswerStyles(answer);
      };

      answer.__awFaqAnchorTransitionEnd = onTransitionEnd;
      answer.addEventListener("transitionend", onTransitionEnd);
    }

    destroy() {
      this.handlers.forEach(({ link, handler }) => {
        link.removeEventListener("click", handler);
      });

      this.handlers = [];
    }
  }

  function init(container = document) {
    container.querySelectorAll(selectors.root).forEach((root) => {
      if (instances.has(root)) {
        return;
      }

      instances.set(root, new AWFaqAnchor(root));
    });
  }

  function destroy(container) {
    container.querySelectorAll(selectors.root).forEach((root) => {
      const instance = instances.get(root);

      if (!instance) {
        return;
      }

      instance.destroy();
      instances.delete(root);
    });
  }

  document.addEventListener("shopify:section:load", (event) => {
    init(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    destroy(event.target);
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => init(document));
  } else {
    init(document);
  }
})();
